import { DELETE, GET, PATCH, POST } from '@/shared/api/requests';
import type {
  CreateRFIData,
  RFIAttachment,
  RFIFilters,
  RFIItem,
  RFIStatus,
  RFIStatusSummaryEntry,
  RFISummary,
  UserMinimal,
  DrawingReference,
  RFIWidgetCounts,
  UpdateRFIData,
} from '../types';

const BASE = '/rfis';

type RFIAttachmentUploadUrlResponse = {
  attachment_id: string;
  gcs_object_path: string;
  signed_upload_url: string;
};

const toResults = <T>(data: unknown): T[] => {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && 'results' in data) {
    return (data as { results?: T[] }).results ?? [];
  }
  return [];
};

export const getRFIs = async (
  projectId: number,
  filters: RFIFilters = {},
): Promise<RFISummary[]> => {
  const params = new URLSearchParams({ project_id: String(projectId) });
  if (filters.status) params.set('status', filters.status);
  if (filters.priority) params.set('priority', filters.priority);
  if (filters.responsible_party) params.set('responsible_party', String(filters.responsible_party));
  if (filters.assignee) params.set('assignee', String(filters.assignee));
  if (filters.overdue) params.set('overdue', 'true');
  if (filters.search) params.set('search', filters.search);
  if (filters.hotspot_id) params.set('hotspot_id', String(filters.hotspot_id));
  if (filters.photo_id) params.set('photo_id', String(filters.photo_id));

  const response = await GET(`${BASE}/items/?${params.toString()}`);
  return toResults<RFISummary>(response.data);
};

export const getRFIById = async (id: string, projectId: number): Promise<RFIItem> => {
  const response = await GET(`${BASE}/items/${id}/?project_id=${projectId}`);
  return response.data as RFIItem;
};

export const createRFI = async (data: CreateRFIData): Promise<RFIItem> => {
  const response = await POST(`${BASE}/items/`, data);
  return response.data as RFIItem;
};

export const updateRFI = async (id: string, data: UpdateRFIData): Promise<RFIItem> => {
  const response = await PATCH(`${BASE}/items/${id}/`, data);
  return response.data as RFIItem;
};

export const deleteRFI = async (id: string): Promise<void> => {
  await DELETE(`${BASE}/items/${id}/`);
};

export const submitRFI = async (id: string): Promise<RFIItem> => {
  const response = await POST(`${BASE}/items/${id}/submit/`, {});
  return response.data as RFIItem;
};

export const assignRFI = async (
  id: string,
  payload: { responsible_party_user_id?: number; assignee_user_id?: number },
): Promise<RFIItem> => {
  const response = await POST(`${BASE}/items/${id}/assign/`, payload);
  return response.data as RFIItem;
};

export const respondToRFI = async (id: string, official_response: string): Promise<RFIItem> => {
  const response = await POST(`${BASE}/items/${id}/respond/`, { official_response });
  return response.data as RFIItem;
};

export const closeRFI = async (id: string, official_response?: string): Promise<RFIItem> => {
  const response = await POST(`${BASE}/items/${id}/close/`, {
    ...(official_response ? { official_response } : {}),
  });
  return response.data as RFIItem;
};

export const reopenRFI = async (id: string): Promise<RFIItem> => {
  const response = await POST(`${BASE}/items/${id}/reopen/`, {});
  return response.data as RFIItem;
};

export const addRFIComment = async (id: string, body: string): Promise<void> => {
  await POST(`${BASE}/items/${id}/comments/`, { body });
};

export const linkRFIAttachmentPhoto = async (
  rfiId: string,
  photoId: number,
): Promise<RFIAttachment> => {
  const response = await POST(`${BASE}/attachments/`, { rfi: rfiId, photo: photoId });
  return response.data as RFIAttachment;
};

export const uploadRFIAttachment = async (rfiId: string, file: File): Promise<RFIAttachment> => {
  const uploadUrlResponse = await POST(`${BASE}/attachments/upload-url/`, {
    rfi_id: rfiId,
    file_name: file.name,
    content_type: file.type || 'application/octet-stream',
  });
  const uploadData = uploadUrlResponse.data as RFIAttachmentUploadUrlResponse;

  await uploadFileToSignedUrl(file, uploadData.signed_upload_url);

  const finalizeResponse = await POST(`${BASE}/attachments/finalize/`, {
    rfi_id: rfiId,
    attachment_id: uploadData.attachment_id,
    gcs_object_path: uploadData.gcs_object_path,
    original_file_name: file.name,
    mime_type: file.type || 'application/octet-stream',
    file_size: file.size,
  });
  return finalizeResponse.data as RFIAttachment;
};

export const deleteRFIAttachment = async (id: string): Promise<void> => {
  await DELETE(`${BASE}/attachments/${id}/`);
};

export const getRFIStatusSummary = async (projectId: number): Promise<RFIStatusSummaryEntry[]> => {
  const response = await GET(`${BASE}/items/status-summary/?project_id=${projectId}`);
  return toResults<RFIStatusSummaryEntry>(response.data);
};

export const getRFIWidgetCounts = async (projectId: number): Promise<RFIWidgetCounts> => {
  const response = await GET(`${BASE}/items/dashboard-widgets/?project_id=${projectId}`);
  return response.data as RFIWidgetCounts;
};

export const getRFIStatusHistory = async (id: string): Promise<{ to_status: RFIStatus }[]> => {
  const response = await GET(`${BASE}/items/${id}/status-history/`);
  return toResults<{ to_status: RFIStatus }>(response.data);
};

export const getProjectShareableUsers = async (projectId: number): Promise<UserMinimal[]> => {
  const response = await GET(`/base/projects/${projectId}/shareable-users/`);
  if (Array.isArray(response.data)) return response.data as UserMinimal[];
  if (response.data?.users) return response.data.users as UserMinimal[];
  return toResults<UserMinimal>(response.data);
};

export const getAreaTagsByProject = async (projectId: number): Promise<{ id: number; name: string }[]> => {
  const layouts = await getProjectLayouts(projectId);
  const responses = await Promise.all(
    layouts.map((layout) => GET(`area-tags/?layout_id=${layout.id}&project_id=${projectId}`))
  );
  const areaTags = responses.flatMap((response) => toResults<{ id: number; name: string }>(response.data));
  return Array.from(new Map(areaTags.map((tag) => [tag.id, tag])).values());
};

export const getProjectLayouts = async (
  projectId: number,
): Promise<{ id: number; name: string; type: string }[]> => {
  const response = await GET(`projects/${projectId}/layouts/`);
  const data = response.data;
  if (Array.isArray(data)) return data;
  if (data.layouts) return data.layouts as { id: number; name: string; type: string }[];
  return toResults<{ id: number; name: string; type: string }>(data);
};

export const createAreaTagForProject = async (payload: {
  name: string;
  project_id: number;
  layout_id: number;
}): Promise<{ id: number; name: string }> => {
  const response = await POST(`/punchlists/locations/create/`, payload);
  return response.data as { id: number; name: string };
};

export const getProjectDrawingSheets = async (projectId: number): Promise<DrawingReference[]> => {
  const response = await GET(`${BASE}/items/drawing-sheets/?project_id=${projectId}`);
  return toResults<DrawingReference>(response.data);
};

const uploadFileToSignedUrl = async (file: File, signedUrl: string): Promise<void> => {
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(new Error(`Attachment upload failed with status ${xhr.status}.`));
    });
    xhr.addEventListener('error', () => {
      reject(new Error('Attachment upload failed.'));
    });
    xhr.open('PUT', signedUrl);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.send(file);
  });
};
